import { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Badge, Spinner, Form, Modal } from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaUsers, FaToggleOn, FaToggleOff,
  FaThLarge, FaList, FaHandshake, FaLightbulb, FaMoneyBillWave,
  FaPalette, FaUserTie, FaLaptopCode, FaBullhorn, FaVideo,
  FaCogs, FaShieldAlt, FaRocket, FaBriefcase, FaSitemap, FaMinusCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import * as departmentService from '../services/firebase/departmentService';
import * as employeeService from '../services/firebase/employeeService';
import { isDeptVisibleToUser } from '../utils/departmentVisibility';
import '../assets/css/employee.css';

const DepartmentsPage = () => {
  const { user } = useAuth();
  const isPrivilegedUser = user?.role === 'hr' || user?.role === 'admin' || user?.role === 'manager';

  // Helper to get department-specific icons
  const getDepartmentIcon = (dept, size = 20) => {
    const code = (dept.code || '').toUpperCase();
    const name = (dept.name || '').toUpperCase();

    let Icon = FaUsers;
    if (code.includes('BD') || name.includes('BUSINESS')) Icon = FaHandshake;
    else if (code.includes('STRATEGY') || name.includes('STRATEGY')) Icon = FaLightbulb;
    else if (code.includes('FINANCE') || name.includes('FINANCE')) Icon = FaMoneyBillWave;
    else if (code.includes('GRAPHIC') || name.includes('DESIGN') || name.includes('CREATIVE')) Icon = FaPalette;
    else if (code.includes('HR') || name.includes('HUMAN') || name.includes('RESOURCES')) Icon = FaUserTie;
    else if (code.includes('IT') || name.includes('TECH') || name.includes('INFORMATION')) Icon = FaLaptopCode;
    else if (code.includes('MARKETING') || name.includes('MARKETING')) Icon = FaBullhorn;
    else if (code.includes('VIDEO') || name.includes('VIDEO') || name.includes('PRODUCTION')) Icon = FaVideo;
    else if (code.includes('OPS') || name.includes('OPERATIONS')) Icon = FaCogs;
    else if (code.includes('SECURITY') || name.includes('SECURITY')) Icon = FaShieldAlt;
    else if (code.includes('SALES') || name.includes('SALES')) Icon = FaBriefcase;

    return <Icon size={size} />;
  };

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // Default to grid

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    parentId: ''
  });

  // Flat list of departments for dropdowns (even when viewing hierarchy)
  const [flatDepartments, setFlatDepartments] = useState([]);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  // Members modal
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [deptMembers, setDeptMembers] = useState([]);
  const [fetchingMembers, setFetchingMembers] = useState(false);
  const [viewingDept, setViewingDept] = useState(null);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      // Fetch both for different uses
      const [hierarchicalRes, flatRes] = await Promise.all([
        departmentService.getDepartments({ hierarchical: true, status: statusFilter, search: searchTerm }),
        departmentService.getDepartments({ status: statusFilter, search: searchTerm })
      ]);
      
      let filteredHierarchical = hierarchicalRes || [];
      let filteredFlat = flatRes || [];

      const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
      if (isDeptOrProdHead) {
        filteredHierarchical = filteredHierarchical.filter(dept => 
          isDeptVisibleToUser(dept.code, dept.name, user)
        );
        filteredFlat = filteredFlat.filter(dept => 
          isDeptVisibleToUser(dept.code, dept.name, user)
        );
      }

      setDepartments(filteredHierarchical);
      setFlatDepartments(filteredFlat);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDepartments();
  };

  const handleOpenModal = (mode, department = null) => {
    setModalMode(mode);
    setSelectedDepartment(department);
    if (department) {
      setFormData({
        code: department.code,
        name: department.name,
        description: department.description || '',
        parentId: department.parentId || ''
      });
    } else {
      setFormData({ code: '', name: '', description: '', parentId: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDepartment(null);
    setFormData({ code: '', name: '', description: '', parentId: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await departmentService.createDepartment(formData);
        toast.success('Department created successfully');
      } else {
        await departmentService.updateDepartment(selectedDepartment.code, formData);
        toast.success('Department updated successfully');
      }
      handleCloseModal();
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error(error.message || 'Failed to save department');
    }
  };

  const handleToggleStatus = async (department) => {
    try {
      const newStatus = department.status === 'active' ? 'inactive' : 'active';
      await departmentService.updateDepartmentStatus(department.code, newStatus);
      toast.success(`Department ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchDepartments();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update department status');
    }
  };

  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await departmentService.deleteDepartment(departmentToDelete.code);
      toast.success('Department deleted successfully');
      setShowDeleteModal(false);
      setDepartmentToDelete(null);
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.message || 'Failed to delete department');
    }
  };

  const handleShowMembers = async (dept) => {
    setViewingDept(dept);
    setShowMembersModal(true);
    setFetchingMembers(true);
    try {
      const response = await employeeService.getEmployees({ department: dept.code });
      let members = response.data || [];
      if (members.length === 0 && dept.name && dept.name !== dept.code) {
        const fallBackResponse = await employeeService.getEmployees({ department: dept.name });
        members = fallBackResponse.data || [];
      }
      setDeptMembers(members);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to fetch department members');
    } finally {
      setFetchingMembers(false);
    }
  };

  const renderDepartmentRow = (dept, level = 0) => {
    return (
      <tr key={dept.code} className={level > 0 ? 'bg-light bg-opacity-10' : ''}>
        <td>
          <div style={{ paddingLeft: `${Math.min(level * 16, 48)}px` }} className="d-flex align-items-center">
            {level > 0 && <span className="text-muted opacity-50 me-2">└──</span>}
            <strong className="text-primary">{dept.code}</strong>
          </div>
        </td>
        <td>
          <div style={{ paddingLeft: `${Math.min(level * 16, 48)}px` }} className="d-flex align-items-center">
            <span className="me-2 text-primary opacity-75">
              {getDepartmentIcon(dept, 18)}
            </span>
            <span className="fw-bold">{dept.name}</span>
          </div>
        </td>
        <td className="small text-muted">{dept.description || '-'}</td>
        <td>
          <Badge bg="info" className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleShowMembers(dept)}>
            <FaUsers className="me-1" /> {dept.employeeCount || 0}
          </Badge>
        </td>
        <td>
          <Badge bg={dept.status === 'active' ? 'success' : 'secondary'}>
            {dept.status.toUpperCase()}
          </Badge>
        </td>
        {isPrivilegedUser && (
          <td>
            <div className="d-flex gap-1">
              <Button variant="outline-warning" size="sm" onClick={() => handleOpenModal('edit', dept)}><FaEdit /></Button>
              <Button 
                variant={dept.status === 'active' ? 'outline-secondary' : 'outline-success'} 
                size="sm" 
                onClick={() => handleToggleStatus(dept)}
              >
                {dept.status === 'active' ? <FaToggleOff /> : <FaToggleOn />}
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(dept)}><FaTrash /></Button>
            </div>
          </td>
        )}
      </tr>
    );
  };

  const renderDepartmentsList = (items, level = 0) => {
    return items.flatMap(dept => [
      renderDepartmentRow(dept, level),
      ...(dept.children ? renderDepartmentsList(dept.children, level + 1) : [])
    ]);
  };

  const renderGridCard = (dept, level = 0) => {
    return (
      <Col key={dept.code} xs={6} sm={6} md={6} lg={4}>
        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden dept-card position-relative">
          {level > 0 && (
            <Badge bg="info" className="position-absolute top-0 end-0 m-1 m-md-2" style={{ fontSize: '0.6rem' }}>
              Sub-Dept
            </Badge>
          )}
          <div className="p-2 p-sm-3 p-md-4 dept-card-body d-flex flex-column h-100">
            <div className="d-flex justify-content-between align-items-start mb-2 mb-md-3">
              <div
                className="bg-primary shadow-sm rounded-4 d-flex align-items-center justify-content-center text-white dept-icon-box"
              >
                {getDepartmentIcon(dept, 26)}
              </div>
              <Badge bg={dept.status === 'active' ? 'success' : 'secondary'} className="rounded-pill dept-badge-status">
                {dept.status.toUpperCase()}
              </Badge>
            </div>
            <h5 className="fw-bold mb-1 dept-card-title text-truncate" title={dept.name}>{dept.name}</h5>
            {dept.parentId && (
              <small className="text-primary fw-bold d-block mb-1 text-truncate dept-parent-text" style={{ fontSize: '0.7rem' }}>
                Parent: {flatDepartments.find(d => d.code === dept.parentId)?.name || dept.parentId}
              </small>
            )}
            <small className="text-muted fw-bold d-block mb-1 mb-md-2 dept-code-text text-truncate">Code: {dept.code}</small>
            <p className="text-muted small mb-2 text-truncate-2 dept-desc-text flex-grow-1" style={{ minHeight: '32px' }}>
              {dept.description || 'No description available.'}
            </p>
            
            <div className="d-flex justify-content-between align-items-center pt-2 pt-md-3 border-top gap-1 flex-wrap mt-auto">
              <div className="text-primary fw-bold small text-truncate dept-members-text" style={{ cursor: 'pointer' }} onClick={() => handleShowMembers(dept)}>
                <FaUsers className="me-1" /> {dept.employeeCount || 0} Members
              </div>
              {isPrivilegedUser && (
                <div className="d-flex gap-1">
                  <Button variant="light" size="sm" className="rounded-circle dept-action-btn d-flex align-items-center justify-content-center" onClick={() => handleOpenModal('edit', dept)} title="Edit">
                    <FaEdit className="text-warning" />
                  </Button>
                  <Button variant="light" size="sm" className="rounded-circle dept-action-btn d-flex align-items-center justify-content-center" onClick={() => handleDeleteClick(dept)} title="Delete">
                    <FaTrash className="text-danger" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Col>
    );
  };

  const renderGridHierarchy = (items, level = 0) => {
    return items.flatMap(dept => [
      renderGridCard(dept, level),
      ...(dept.children ? renderGridHierarchy(dept.children, level + 1) : [])
    ]);
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="flex-grow-1 employee-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4">
          {/* Header */}
          <Row className="mb-3 mb-md-4 align-items-center g-2">
            <Col xs={12} md={7}>
              <h2 className="fw-bold text-dark mb-1 fs-4 fs-md-2">Organization Hierarchy</h2>
              <p className="text-muted mb-0 small">Manage departments and branches</p>
            </Col>
            <Col xs={12} md={5} className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mt-md-0 flex-nowrap">
              <div className="employee-view-toggle">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <FaThLarge size={12} /> <span className="fw-bold ms-1">Grid</span>
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'light'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <FaList size={12} /> <span className="fw-bold ms-1">Hierarchy</span>
                </Button>
              </div>
              {isPrivilegedUser && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleOpenModal('create')}
                  className="shadow-sm fw-bold px-3 rounded-pill d-inline-flex align-items-center text-nowrap"
                  style={{ height: '38px', fontSize: '0.84rem' }}
                >
                  <FaPlus className="me-1" size={11} /> <span>Add Department</span>
                </Button>
              )}
            </Col>
          </Row>

          {/* Search and Filters */}
          <Row className="mb-3 g-2 align-items-center">
            <Col xs={12} md={4}>
              <Form onSubmit={handleSearch}>
                <Form.Control
                  type="text"
                  placeholder="Search departments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                  className="rounded-3"
                />
              </Form>
            </Col>
            <Col xs={7} sm={6} md={3}>
              <Form.Select
                value={statusFilter}
                size="sm"
                className="rounded-3"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setTimeout(fetchDepartments, 100);
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col xs={5} sm={6} md={2}>
              <Button 
                variant="outline-secondary" 
                size="sm"
                className="w-100 rounded-3 fw-bold"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setTimeout(fetchDepartments, 100);
                }}
              >
                Clear
              </Button>
            </Col>
          </Row>

          {/* Departments Table / Grid */}
          <div className="bg-white rounded-4 shadow-sm p-2 p-md-3 border">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading organization structure...</p>
              </div>
            ) : viewMode === 'list' ? (
              <Table hover responsive className="align-middle border-top mb-0 text-nowrap">
                <thead className="bg-light">
                  <tr>
                    <th>Code</th>
                    <th>Department Name</th>
                    <th>Description</th>
                    <th>Employees</th>
                    <th>Status</th>
                    {isPrivilegedUser && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {departments.length === 0 ? (
                    <tr>
                      <td colSpan={isPrivilegedUser ? 6 : 5} className="text-center py-4 text-muted">No structures found</td>
                    </tr>
                  ) : (
                    renderDepartmentsList(departments)
                  )}
                </tbody>
              </Table>
            ) : (
              <Row className="g-2 g-md-3">
                {departments.length === 0 ? (
                  <Col xs={12} className="text-center py-5 text-muted">No structures found</Col>
                ) : (
                  renderGridHierarchy(departments)
                )}
              </Row>
            )}
          </div>
        </Container>
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'create' ? 'Add Department' : 'Edit Department'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Department Code *</Form.Label>
              <Form.Control
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                disabled={modalMode === 'edit'}
                maxLength={10}
              />
              <Form.Text className="text-muted">
                2-10 characters, alphanumeric only
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Department Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                maxLength={100}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Parent Department</Form.Label>
              <Form.Select
                value={formData.parentId}
                onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
              >
                <option value="">None (Top Level)</option>
                {flatDepartments
                  .filter(d => d.code !== selectedDepartment?.code) // Prevent circular ref
                  .map(dept => (
                    <option key={dept.code} value={dept.code}>{dept.name} ({dept.code})</option>
                  ))
                }
              </Form.Select>
              <Form.Text className="text-muted">
                Select a parent if this is a sub-department (e.g. Video under Production).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {departmentToDelete && (
            <>
              <p>Are you sure you want to delete the department <strong>{departmentToDelete.name}</strong>?</p>
              {departmentToDelete.employeeCount > 0 && (
                <div className="alert alert-warning">
                  <strong>Warning:</strong> This department has {departmentToDelete.employeeCount} employee(s) assigned.
                  Consider deactivating instead of deleting.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Department Members Modal */}
      <Modal
        show={showMembersModal}
        onHide={() => setShowMembersModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaUsers className="me-2" />
            Members: {viewingDept?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fetchingMembers ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Fetching members...</p>
            </div>
          ) : deptMembers.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaUsers size={48} className="mb-3 opacity-25" />
              <p>No members found in this department.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>ID</th>
                    <th>Designation</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deptMembers.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px', overflow: 'hidden' }}
                          >
                            {member.personal?.photo || member.photo ? (
                              <img src={member.personal?.photo || member.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span className="fw-bold text-primary">
                                {(member.firstName?.[0] || '') + (member.lastName?.[0] || '')}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{member.personal?.fullName || `${member.firstName} ${member.lastName}`}</div>
                            <div className="small text-muted">{member.personal?.email || member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="small fw-bold text-muted">{member.job?.employeeCode || member.employeeId}</td>
                      <td className="small">{member.job?.designation || member.designation}</td>
                      <td>
                        <Badge bg={member.status === 'Active' ? 'success' : 'secondary'} className="rounded-pill px-2">
                          {member.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMembersModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DepartmentsPage;
